import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faFilter,
  faCalendarAlt,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";

const ActiveFiltersDisplay = ({
  selectedCustomers,
  selectedDivisions,
  selectedRfcs,
  selectedOperationTypes,
  selectedEntryPoints,
  selectedPedimentos,
  selectedStatus,
  selectedPurchaseOrders,
  selectedBillOfLadings,
  selectedClavePeds,
  selectedRegimes,
  selectedTrafficStatus,
  weightRange,
  packagesRange,
  dateRange,
  searchTerm,
  setSelectedCustomers,
  setSelectedDivisions,
  setSelectedRfcs,
  setSelectedOperationTypes,
  setSelectedEntryPoints,
  setSelectedPedimentos,
  setSelectedStatus,
  setSelectedPurchaseOrders,
  setSelectedBillOfLadings,
  setSelectedClavePeds,
  setSelectedRegimes,
  setSelectedTrafficStatus,
  setWeightRange,
  setPackagesRange,
  setDateRange,
  setSearchTerm,
  clearAllFilters,
  clearFilterType,
}) => {
  // Check if any filters are active
  const hasActiveFilters =
    selectedCustomers.length > 0 ||
    selectedDivisions.length > 0 ||
    selectedRfcs.length > 0 ||
    selectedOperationTypes.length > 0 ||
    selectedEntryPoints.length > 0 ||
    selectedPedimentos.length > 0 ||
    selectedStatus.length > 0 ||
    selectedPurchaseOrders.length > 0 ||
    selectedBillOfLadings.length > 0 ||
    selectedClavePeds.length > 0 ||
    selectedRegimes.length > 0 ||
    selectedTrafficStatus.length > 0 ||
    (weightRange.min !== "" || weightRange.max !== "") ||
    (packagesRange.min !== "" || packagesRange.max !== "") ||
    (dateRange[0].startDate && dateRange[0].endDate) ||
    searchTerm;

  if (!hasActiveFilters) return null;

  // Count total active filters
  const totalFilters =
    selectedCustomers.length +
    selectedDivisions.length +
    selectedRfcs.length +
    selectedOperationTypes.length +
    selectedEntryPoints.length +
    selectedPedimentos.length +
    selectedStatus.length +
    selectedPurchaseOrders.length +
    selectedBillOfLadings.length +
    selectedClavePeds.length +
    selectedRegimes.length +
    selectedTrafficStatus.length +
    (weightRange.min !== "" || weightRange.max !== "" ? 1 : 0) +
    (packagesRange.min !== "" || packagesRange.max !== "" ? 1 : 0) +
    (dateRange[0].startDate && dateRange[0].endDate ? 1 : 0) +
    (searchTerm ? 1 : 0);

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-4 mb-4 animate-fadeIn relative z-20">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <FontAwesomeIcon icon={faFilter} className="text-blue-600 mr-2" />
          <h3 className="font-medium text-gray-800">
            Active Filters
            <span className="ml-2 bg-blue-600 text-white text-xs font-bold py-0.5 px-2 rounded-full">
              {totalFilters}
            </span>
          </h3>
        </div>
        <button
          onClick={clearAllFilters}
          className="text-sm font-medium text-red-600 hover:text-red-800 transition-colors"
        >
          Clear All
        </button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {searchTerm && (
          <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded-md flex items-center text-xs border border-purple-100">
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            <span className="font-medium mr-1">Search:</span> "{searchTerm}"
            <button
              onClick={() => setSearchTerm("")}
              className="ml-1 text-purple-500 hover:text-purple-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        )}

        {dateRange[0].startDate && dateRange[0].endDate && (
          <span className="bg-green-50 text-green-700 px-2 py-1 rounded-md flex items-center text-xs border border-green-100">
            <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
            <span className="font-medium mr-1">Date Range:</span>
            {new Date(dateRange[0].startDate).toLocaleDateString()} -{" "}
            {new Date(dateRange[0].endDate).toLocaleDateString()}
            <button
              onClick={() => clearFilterType("dateRange")}
              className="ml-1 text-green-500 hover:text-green-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        )}

        {selectedCustomers.map((item) => (
          <span
            key={item.value}
            className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md flex items-center text-xs border border-blue-100 hover:bg-blue-100 transition-colors"
          >
            <span className="font-medium mr-1">Customer:</span> {item.label}
            <button
              onClick={() =>
                setSelectedCustomers((prev) =>
                  prev.filter((i) => i.value !== item.value)
                )
              }
              className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        ))}

        {selectedDivisions.map((item) => (
          <span
            key={item.value}
            className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md flex items-center text-xs border border-indigo-100 hover:bg-indigo-100 transition-colors"
          >
            <span className="font-medium mr-1">Division:</span> {item.label}
            <button
              onClick={() =>
                setSelectedDivisions((prev) =>
                  prev.filter((i) => i.value !== item.value)
                )
              }
              className="ml-1 text-indigo-500 hover:text-indigo-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        ))}

        {selectedRfcs.map((item) => (
          <span
            key={item.value}
            className="bg-cyan-50 text-cyan-700 px-2 py-1 rounded-md flex items-center text-xs border border-cyan-100 hover:bg-cyan-100 transition-colors"
          >
            <span className="font-medium mr-1">RFC:</span> {item.label}
            <button
              onClick={() =>
                setSelectedRfcs((prev) =>
                  prev.filter((i) => i.value !== item.value)
                )
              }
              className="ml-1 text-cyan-500 hover:text-cyan-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        ))}

        {selectedOperationTypes.map((item) => (
          <span
            key={item.value}
            className="bg-amber-50 text-amber-700 px-2 py-1 rounded-md flex items-center text-xs border border-amber-100 hover:bg-amber-100 transition-colors"
          >
            <span className="font-medium mr-1">Op. Type:</span> {item.label}
            <button
              onClick={() =>
                setSelectedOperationTypes((prev) =>
                  prev.filter((i) => i.value !== item.value)
                )
              }
              className="ml-1 text-amber-500 hover:text-amber-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        ))}

        {selectedEntryPoints.map((item) => (
          <span
            key={item.value}
            className="bg-teal-50 text-teal-700 px-2 py-1 rounded-md flex items-center text-xs border border-teal-100 hover:bg-teal-100 transition-colors"
          >
            <span className="font-medium mr-1">Entry:</span> {item.label}
            <button
              onClick={() =>
                setSelectedEntryPoints((prev) =>
                  prev.filter((i) => i.value !== item.value)
                )
              }
              className="ml-1 text-teal-500 hover:text-teal-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        ))}

        {selectedPedimentos.map((item) => (
          <span
            key={item.value}
            className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md flex items-center text-xs border border-blue-100 hover:bg-blue-100 transition-colors"
          >
            <span className="font-medium mr-1">Pedimento:</span> {item.label}
            <button
              onClick={() =>
                setSelectedPedimentos((prev) =>
                  prev.filter((i) => i.value !== item.value)
                )
              }
              className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        ))}

        {selectedStatus.map((item) => (
          <span
            key={item.value}
            className="bg-rose-50 text-rose-700 px-2 py-1 rounded-md flex items-center text-xs border border-rose-100 hover:bg-rose-100 transition-colors"
          >
            <span className="font-medium mr-1">Status:</span> {item.label}
            <button
              onClick={() =>
                setSelectedStatus((prev) =>
                  prev.filter((i) => i.value !== item.value)
                )
              }
              className="ml-1 text-rose-500 hover:text-rose-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        ))}

        {selectedPurchaseOrders.map((item) => (
          <span
            key={item.value}
            className="bg-orange-50 text-orange-700 px-2 py-1 rounded-md flex items-center text-xs border border-orange-100 hover:bg-orange-100 transition-colors"
          >
            <span className="font-medium mr-1">PO:</span> {item.label}
            <button
              onClick={() =>
                setSelectedPurchaseOrders((prev) =>
                  prev.filter((i) => i.value !== item.value)
                )
              }
              className="ml-1 text-orange-500 hover:text-orange-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        ))}

        {selectedBillOfLadings.map((item) => (
          <span
            key={item.value}
            className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded-md flex items-center text-xs border border-emerald-100 hover:bg-emerald-100 transition-colors"
          >
            <span className="font-medium mr-1">BOL:</span> {item.label}
            <button
              onClick={() =>
                setSelectedBillOfLadings((prev) =>
                  prev.filter((i) => i.value !== item.value)
                )
              }
              className="ml-1 text-emerald-500 hover:text-emerald-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        ))}

        {selectedClavePeds.map((item) => (
          <span
            key={item.value}
            className="bg-violet-50 text-violet-700 px-2 py-1 rounded-md flex items-center text-xs border border-violet-100 hover:bg-violet-100 transition-colors"
          >
            <span className="font-medium mr-1">Clave:</span> {item.label}
            <button
              onClick={() =>
                setSelectedClavePeds((prev) =>
                  prev.filter((i) => i.value !== item.value)
                )
              }
              className="ml-1 text-violet-500 hover:text-violet-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        ))}

        {selectedRegimes.map((item) => (
          <span
            key={item.value}
            className="bg-pink-50 text-pink-700 px-2 py-1 rounded-md flex items-center text-xs border border-pink-100 hover:bg-pink-100 transition-colors"
          >
            <span className="font-medium mr-1">Regime:</span> {item.label}
            <button
              onClick={() =>
                setSelectedRegimes((prev) =>
                  prev.filter((i) => i.value !== item.value)
                )
              }
              className="ml-1 text-pink-500 hover:text-pink-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        ))}

        {selectedTrafficStatus.map((item) => (
          <span
            key={item.value}
            className="bg-sky-50 text-sky-700 px-2 py-1 rounded-md flex items-center text-xs border border-sky-100 hover:bg-sky-100 transition-colors"
          >
            <span className="font-medium mr-1">Traffic:</span> {item.label}
            <button
              onClick={() =>
                setSelectedTrafficStatus((prev) =>
                  prev.filter((i) => i.value !== item.value)
                )
              }
              className="ml-1 text-sky-500 hover:text-sky-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        ))}

        {(weightRange.min !== "" || weightRange.max !== "") && (
          <span className="bg-slate-50 text-slate-700 px-2 py-1 rounded-md flex items-center text-xs border border-slate-100 hover:bg-slate-100 transition-colors">
            <span className="font-medium mr-1">Weight:</span> 
            {weightRange.min || '0'} - {weightRange.max || '∞'}
            <button
              onClick={() => setWeightRange({ min: "", max: "" })}
              className="ml-1 text-slate-500 hover:text-slate-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        )}

        {(packagesRange.min !== "" || packagesRange.max !== "") && (
          <span className="bg-gray-50 text-gray-700 px-2 py-1 rounded-md flex items-center text-xs border border-gray-100 hover:bg-gray-100 transition-colors">
            <span className="font-medium mr-1">Packages:</span> 
            {packagesRange.min || '0'} - {packagesRange.max || '∞'}
            <button
              onClick={() => setPackagesRange({ min: "", max: "" })}
              className="ml-1 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </span>
        )}
      </div>
    </div>
  );
};

export default ActiveFiltersDisplay;
